import { CalendarOutlined, DeleteOutlined, ReloadOutlined, SaveOutlined, SettingOutlined } from '@ant-design/icons';
import {
  Button,
  DatePicker,
  Form,
  InputNumber,
  message,
  Modal,
  Select,
  Space,
  Switch,
  Table,
  Tabs,
  Tag,
  Typography,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs, { Dayjs } from 'dayjs';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import AdminLayout from '../components/AdminLayout';
import { calendarConfigService } from '../services/calendarConfigService';
import { employeeUserService } from '../services/employeeUserService';
import { workScheduleService } from '../services/workScheduleService';
import { CalendarConfig } from '../types/calendarConfig';
import { EmployeeUser } from '../types/employeeUser';
import { DaySchedule, ScheduleEntry, WorkPosition } from '../types/workSchedule';
import '../styles/AdminPages.css';

const { Text } = Typography;

const DAYS_OF_WEEK = ['Thứ 2', 'Thứ 3', 'Thứ 4', 'Thứ 5', 'Thứ 6', 'Thứ 7', 'Chủ nhật'];
const DAY_SHORT_LABELS = ['T2', 'T3', 'T4', 'T5', 'T6', 'T7', 'CN'];

type ShiftKey = 'morning' | 'afternoon';
type TabKey = 'registration' | 'summary' | 'settings';

interface WeekScheduleCacheEntry {
  schedules: ScheduleEntry[];
  isArchive: boolean;
}

interface DeadlineCheckResult {
  isPast: boolean;
  deadlineText?: string;
}

const normalizeWeekStart = (input: Date): Date => {
  const date = new Date(input);
  const day = date.getDay();
  const diffToMonday = day === 0 ? -6 : 1 - day;
  date.setDate(date.getDate() + diffToMonday);
  date.setHours(0, 0, 0, 0);
  return date;
};

const getNextWeek = (): Dayjs => {
  const now = new Date();
  const monday = normalizeWeekStart(now);
  monday.setDate(monday.getDate() + 7);
  return dayjs(monday);
};

const getCurrentWeek = (): Dayjs => {
  return dayjs(normalizeWeekStart(new Date()));
};

const getWeekKey = (input: Date): string => {
  return dayjs(normalizeWeekStart(input)).format('YYYY-MM-DD');
};

const emptyDaySchedule = (): DaySchedule => ({
  morning: false,
  afternoon: false,
  morningPosition: 'none',
  afternoonPosition: 'none',
});

const buildEmptyWeekSchedule = (): Record<number, DaySchedule> => {
  const schedule: Record<number, DaySchedule> = {};
  for (let index = 0; index < 7; index += 1) {
    schedule[index] = emptyDaySchedule();
  }
  return schedule;
};

const countShiftOfSchedule = (schedule: Record<number, DaySchedule>): number => {
  return Object.values(schedule).reduce((total, dayData) => {
    return total + (dayData.morning ? 1 : 0) + (dayData.afternoon ? 1 : 0);
  }, 0);
};

const hasAnyRegisteredShift = (schedule: Record<number, DaySchedule>, storeFilter: string): boolean => {
  return Object.values(schedule).some((dayData) => {
    const morningMatched = dayData.morning && (storeFilter === 'all' || dayData.morningStoreLocation === storeFilter);
    const afternoonMatched = dayData.afternoon && (storeFilter === 'all' || dayData.afternoonStoreLocation === storeFilter);
    return morningMatched || afternoonMatched;
  });
};

const getPositionText = (position?: WorkPosition): string => {
  if (position === 'table') {
    return 'Table';
  }
  if (position === 'bar') {
    return 'Bar';
  }
  if (position === 'security') {
    return 'Security';
  }
  return 'Nghỉ';
};

const ScheduleRegistrationEngine: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabKey>('registration');
  const [employees, setEmployees] = useState<EmployeeUser[]>([]);
  const [selectedWeek, setSelectedWeek] = useState<Dayjs>(getNextWeek());
  const [selectedUserId, setSelectedUserId] = useState<string>('');

  const [weekSchedules, setWeekSchedules] = useState<ScheduleEntry[]>([]);
  const [currentWeekSchedules, setCurrentWeekSchedules] = useState<ScheduleEntry[]>([]);
  const [selectedSchedule, setSelectedSchedule] = useState<Record<number, DaySchedule>>(buildEmptyWeekSchedule());
  const [loadingSchedules, setLoadingSchedules] = useState<boolean>(false);
  const [loadingCurrentWeekSchedules, setLoadingCurrentWeekSchedules] = useState<boolean>(false);
  const [savingSchedule, setSavingSchedule] = useState<boolean>(false);
  const [isArchiveWeek, setIsArchiveWeek] = useState<boolean>(false);

  const [summaryStoreFilter, setSummaryStoreFilter] = useState<string>('all');
  const [detailSchedule, setDetailSchedule] = useState<ScheduleEntry | null>(null);

  const [calendarConfig, setCalendarConfig] = useState<CalendarConfig>(calendarConfigService.getDefaultConfig());
  const [savingConfig, setSavingConfig] = useState<boolean>(false);
  const [resettingSchedules, setResettingSchedules] = useState<boolean>(false);
  const [cleaningDuplicates, setCleaningDuplicates] = useState<boolean>(false);
  const [configForm] = Form.useForm();

  const isMountedRef = useRef<boolean>(true);
  const weekScheduleCacheRef = useRef<Map<string, WeekScheduleCacheEntry>>(new Map());
  const currentWeekCacheRef = useRef<ScheduleEntry[] | null>(null);
  const deadlineCheckCacheRef = useRef<Map<string, DeadlineCheckResult>>(new Map());
  const employeesRequestRef = useRef<Promise<EmployeeUser[]> | null>(null);
  const configRequestRef = useRef<Promise<CalendarConfig> | null>(null);
  const weekSchedulesRequestRef = useRef<Promise<void> | null>(null);
  const currentWeekSchedulesRequestRef = useRef<Promise<void> | null>(null);
  const weekSchedulesRequestIdRef = useRef<number>(0);
  const currentWeekSchedulesRequestIdRef = useRef<number>(0);

  const selectedEmployee = useMemo(
    () => employees.find((employee) => employee.userId === selectedUserId) ?? null,
    [employees, selectedUserId]
  );

  const currentEmployeeSchedule = useMemo(
    () => weekSchedules.find((item) => item.userId === selectedUserId) ?? null,
    [weekSchedules, selectedUserId]
  );

  const storeOptions = useMemo(() => {
    return (calendarConfig.storeLocations.length > 0
      ? calendarConfig.storeLocations
      : ['Cửa hàng 1'])
      .filter((item, index, array) => array.indexOf(item) === index)
      .map((item) => ({ label: item, value: item }));
  }, [calendarConfig.storeLocations]);

  const summarySchedules = useMemo(() => {
    if (summaryStoreFilter === 'all') {
      return weekSchedules;
    }

    return weekSchedules.filter((item) => {
      return Object.values(item.schedule).some((dayData) => {
        return dayData.morningStoreLocation === summaryStoreFilter || dayData.afternoonStoreLocation === summaryStoreFilter;
      });
    });
  }, [summaryStoreFilter, weekSchedules]);

  const currentWeekMatrixRows = useMemo(() => {
    const scheduleByUser = new Map<string, ScheduleEntry>();
    currentWeekSchedules.forEach((item) => {
      scheduleByUser.set(item.userId, item);
    });

    return employees.map((employee) => {
      const matchedSchedule = scheduleByUser.get(employee.userId);
      return {
        userId: employee.userId,
        userName: employee.name,
        userAvatar: employee.avatar,
        schedule: matchedSchedule?.schedule ?? buildEmptyWeekSchedule(),
      };
    }).filter((row) => hasAnyRegisteredShift(row.schedule, summaryStoreFilter));
  }, [currentWeekSchedules, employees, summaryStoreFilter]);

  const loadEmployees = useCallback(async (force = false) => {
    if (!force && employeesRequestRef.current) {
      await employeesRequestRef.current;
      return;
    }

    const request = employeeUserService.getAllUsersDeduplicated();
    employeesRequestRef.current = request;
    try {
      const data = await request;
      if (!isMountedRef.current) {
        return;
      }

      setEmployees(data);
      setSelectedUserId((prev) => (prev || data.length === 0 ? prev : data[0].userId));
    } catch (error) {
      if (isMountedRef.current) {
        message.error('Không thể tải danh sách nhân viên');
      }
    } finally {
      if (employeesRequestRef.current === request) {
        employeesRequestRef.current = null;
      }
    }
  }, []);

  const loadConfig = useCallback(async (force = false) => {
    if (!force && configRequestRef.current) {
      await configRequestRef.current;
      return;
    }

    const request = calendarConfigService.getCalendarConfig();
    configRequestRef.current = request;
    try {
      const data = await request;
      if (!isMountedRef.current) {
        return;
      }

      setCalendarConfig(data);
      configForm.setFieldsValue(data);
    } catch (error) {
      if (isMountedRef.current) {
        message.error('Không thể tải cấu hình lịch làm việc');
      }
    } finally {
      if (configRequestRef.current === request) {
        configRequestRef.current = null;
      }
    }
  }, [configForm]);

  const loadSchedulesByWeek = useCallback(async (week: Date, options?: { force?: boolean }) => {
    const force = options?.force === true;
    const normalizedWeek = normalizeWeekStart(week);
    const weekKey = getWeekKey(normalizedWeek);

    if (!force) {
      const cached = weekScheduleCacheRef.current.get(weekKey);
      if (cached) {
        setWeekSchedules(cached.schedules);
        setIsArchiveWeek(cached.isArchive);
        return;
      }

      if (weekSchedulesRequestRef.current) {
        await weekSchedulesRequestRef.current;
        const cachedAfterWait = weekScheduleCacheRef.current.get(weekKey);
        if (cachedAfterWait) {
          setWeekSchedules(cachedAfterWait.schedules);
          setIsArchiveWeek(cachedAfterWait.isArchive);
        }
        return;
      }
    }

    const requestId = weekSchedulesRequestIdRef.current + 1;
    weekSchedulesRequestIdRef.current = requestId;
    setLoadingSchedules(true);

    const request = (async () => {
      try {
        const currentSchedules = await workScheduleService.getAllSchedulesForWeek(normalizedWeek);
        let schedulesToUse = currentSchedules;
        let archiveFlag = false;

        if (currentSchedules.length === 0) {
          const archived = await workScheduleService.getArchivedSchedulesForWeek(normalizedWeek);
          schedulesToUse = archived;
          archiveFlag = archived.length > 0;
        }

        weekScheduleCacheRef.current.set(weekKey, {
          schedules: schedulesToUse,
          isArchive: archiveFlag,
        });

        if (!isMountedRef.current || requestId !== weekSchedulesRequestIdRef.current) {
          return;
        }

        setWeekSchedules(schedulesToUse);
        setIsArchiveWeek(archiveFlag);
      } catch (error) {
        if (!isMountedRef.current || requestId !== weekSchedulesRequestIdRef.current) {
          return;
        }

        message.error('Không thể tải lịch làm việc theo tuần');
        setWeekSchedules([]);
        setIsArchiveWeek(false);
      } finally {
        if (requestId === weekSchedulesRequestIdRef.current) {
          weekSchedulesRequestRef.current = null;
        }
        if (isMountedRef.current && requestId === weekSchedulesRequestIdRef.current) {
          setLoadingSchedules(false);
        }
      }
    })();

    weekSchedulesRequestRef.current = request;
    await request;
  }, []);

  const loadCurrentWeekSchedules = useCallback(async (force = false) => {
    if (!force && currentWeekCacheRef.current) {
      setCurrentWeekSchedules(currentWeekCacheRef.current);
      return;
    }

    if (!force && currentWeekSchedulesRequestRef.current) {
      await currentWeekSchedulesRequestRef.current;
      return;
    }

    const requestId = currentWeekSchedulesRequestIdRef.current + 1;
    currentWeekSchedulesRequestIdRef.current = requestId;
    setLoadingCurrentWeekSchedules(true);

    const request = (async () => {
      try {
        const currentWeekStart = getCurrentWeek().toDate();
        const currentSchedules = await workScheduleService.getAllSchedulesForWeek(currentWeekStart);
        const schedulesToUse =
          currentSchedules.length > 0
            ? currentSchedules
            : await workScheduleService.getArchivedSchedulesForWeek(currentWeekStart);

        currentWeekCacheRef.current = schedulesToUse;

        if (!isMountedRef.current || requestId !== currentWeekSchedulesRequestIdRef.current) {
          return;
        }

        setCurrentWeekSchedules(schedulesToUse);
      } catch (error) {
        if (!isMountedRef.current || requestId !== currentWeekSchedulesRequestIdRef.current) {
          return;
        }

        message.error('Không thể tải lịch tuần hiện tại');
        setCurrentWeekSchedules([]);
      } finally {
        if (requestId === currentWeekSchedulesRequestIdRef.current) {
          currentWeekSchedulesRequestRef.current = null;
        }
        if (isMountedRef.current && requestId === currentWeekSchedulesRequestIdRef.current) {
          setLoadingCurrentWeekSchedules(false);
        }
      }
    })();

    currentWeekSchedulesRequestRef.current = request;
    await request;
  }, []);

  const getDeadlineStatus = useCallback(async (week: Date): Promise<DeadlineCheckResult> => {
    const weekKey = getWeekKey(week);
    const cached = deadlineCheckCacheRef.current.get(weekKey);
    if (cached) {
      return cached;
    }

    const isPast = await calendarConfigService.isPastDeadline(week);
    if (!isPast) {
      const result = { isPast: false };
      deadlineCheckCacheRef.current.set(weekKey, result);
      return result;
    }

    const deadlineText = await calendarConfigService.getFormattedDeadline(week);
    const result = { isPast: true, deadlineText };
    deadlineCheckCacheRef.current.set(weekKey, result);
    return result;
  }, []);

  const handleWeekChange = useCallback((value: Dayjs | null) => {
    if (!value) {
      return;
    }

    const normalized = dayjs(normalizeWeekStart(value.toDate()));
    setSelectedWeek((prev) => (prev.isSame(normalized, 'day') ? prev : normalized));
  }, []);

  useEffect(() => {
    isMountedRef.current = true;
    loadEmployees();
    loadConfig();
    loadCurrentWeekSchedules();
    return () => {
      isMountedRef.current = false;
    };
  }, [loadEmployees, loadConfig, loadCurrentWeekSchedules]);

  useEffect(() => {
    loadSchedulesByWeek(selectedWeek.toDate());
  }, [selectedWeek, loadSchedulesByWeek]);

  useEffect(() => {
    if (!selectedUserId) {
      setSelectedSchedule(buildEmptyWeekSchedule());
      return;
    }

    if (currentEmployeeSchedule) {
      setSelectedSchedule({
        ...buildEmptyWeekSchedule(),
        ...currentEmployeeSchedule.schedule,
      });
      return;
    }

    setSelectedSchedule(buildEmptyWeekSchedule());
  }, [currentEmployeeSchedule, selectedUserId]);

  const countShiftOccupancy = (
    dayIndex: number,
    shift: ShiftKey,
    storeLocation: string,
    position?: WorkPosition
  ): number => {
    return weekSchedules
      .filter((item) => item.userId !== selectedUserId)
      .reduce((total, item) => {
        const day = item.schedule[dayIndex];
        if (!day) {
          return total;
        }

        const isActive = shift === 'morning' ? day.morning : day.afternoon;
        if (!isActive) {
          return total;
        }

        const dayStore = shift === 'morning' ? day.morningStoreLocation : day.afternoonStoreLocation;
        if (dayStore !== storeLocation) {
          return total;
        }

        if (!position || position === 'none') {
          return total + 1;
        }

        const dayPosition = shift === 'morning' ? day.morningPosition : day.afternoonPosition;
        return dayPosition === position ? total + 1 : total;
      }, 0);
  };

  const canAssignShift = (dayIndex: number, storeLocation: string): boolean => {
    const maxShiftCapacity = calendarConfigService.getShiftCapacityForDay(dayIndex, calendarConfig);
    const occupied = countShiftOccupancy(dayIndex, 'morning', storeLocation) + countShiftOccupancy(dayIndex, 'afternoon', storeLocation);
    return occupied < maxShiftCapacity * 2;
  };

  const toggleShift = async (dayIndex: number, shift: ShiftKey, checked: boolean) => {
    if (!selectedEmployee) {
      message.warning('Vui lòng chọn nhân viên để đăng ký lịch');
      return;
    }

    if (!calendarConfig.calendarEnabled) {
      message.error('Tính năng đăng ký lịch đang tạm tắt');
      return;
    }

    if (isArchiveWeek) {
      message.error('Không thể chỉnh sửa dữ liệu lịch đã archive');
      return;
    }

    const defaultStore = storeOptions[0]?.value ?? 'Cửa hàng 1';
    const dayData = selectedSchedule[dayIndex] ?? emptyDaySchedule();
    const targetStore =
      shift === 'morning'
        ? dayData.morningStoreLocation ?? defaultStore
        : dayData.afternoonStoreLocation ?? defaultStore;

    if (checked) {
      const deadlineStatus = await getDeadlineStatus(selectedWeek.toDate());
      const hasExisting = Boolean(currentEmployeeSchedule);
      if (deadlineStatus.isPast && !hasExisting) {
        message.error(`Đã quá hạn đăng ký lịch. Hạn đăng ký: ${deadlineStatus.deadlineText}`);
        return;
      }

      if (!canAssignShift(dayIndex, targetStore)) {
        message.error('Ca làm đã đủ số lượng theo giới hạn cửa hàng');
        return;
      }

      setSelectedSchedule((prev) => {
        const nextDay = prev[dayIndex] ?? emptyDaySchedule();
        const nextPosition = selectedEmployee.defaultPosition ?? 'none';
        return {
          ...prev,
          [dayIndex]: {
            ...nextDay,
            morning: shift === 'morning' ? true : nextDay.morning,
            afternoon: shift === 'afternoon' ? true : nextDay.afternoon,
            morningPosition: shift === 'morning' ? nextPosition : nextDay.morningPosition,
            afternoonPosition: shift === 'afternoon' ? nextPosition : nextDay.afternoonPosition,
            morningStoreLocation: shift === 'morning' ? targetStore : nextDay.morningStoreLocation,
            afternoonStoreLocation: shift === 'afternoon' ? targetStore : nextDay.afternoonStoreLocation,
          },
        };
      });
      return;
    }

    setSelectedSchedule((prev) => {
      const nextDay = prev[dayIndex] ?? emptyDaySchedule();
      return {
        ...prev,
        [dayIndex]: {
          ...nextDay,
          morning: shift === 'morning' ? false : nextDay.morning,
          afternoon: shift === 'afternoon' ? false : nextDay.afternoon,
          morningPosition: shift === 'morning' ? 'none' : nextDay.morningPosition,
          afternoonPosition: shift === 'afternoon' ? 'none' : nextDay.afternoonPosition,
          morningStoreLocation: shift === 'morning' ? undefined : nextDay.morningStoreLocation,
          afternoonStoreLocation: shift === 'afternoon' ? undefined : nextDay.afternoonStoreLocation,
        },
      };
    });
  };

  const updatePosition = (dayIndex: number, shift: ShiftKey, position: WorkPosition) => {
    const day = selectedSchedule[dayIndex] ?? emptyDaySchedule();
    const storeLocation =
      shift === 'morning' ? day.morningStoreLocation ?? storeOptions[0]?.value : day.afternoonStoreLocation ?? storeOptions[0]?.value;

    if (!storeLocation) {
      message.error('Vui lòng chọn cửa hàng trước khi chọn vị trí');
      return;
    }

    if (position !== 'none') {
      const capacities = calendarConfigService.getPositionCapacitiesForDay(dayIndex, calendarConfig);
      const maxCapacity = capacities[position as 'table' | 'bar' | 'security'];
      const occupied = countShiftOccupancy(dayIndex, shift, storeLocation, position);
      if (occupied >= maxCapacity) {
        message.error('Vị trí đã đủ số lượng cho ca này');
        return;
      }
    }

    setSelectedSchedule((prev) => {
      const nextDay = prev[dayIndex] ?? emptyDaySchedule();
      return {
        ...prev,
        [dayIndex]: {
          ...nextDay,
          morningPosition: shift === 'morning' ? position : nextDay.morningPosition,
          afternoonPosition: shift === 'afternoon' ? position : nextDay.afternoonPosition,
        },
      };
    });
  };

  const updateStoreLocation = (dayIndex: number, shift: ShiftKey, storeLocation: string) => {
    setSelectedSchedule((prev) => {
      const nextDay = prev[dayIndex] ?? emptyDaySchedule();
      return {
        ...prev,
        [dayIndex]: {
          ...nextDay,
          morningStoreLocation: shift === 'morning' ? storeLocation : nextDay.morningStoreLocation,
          afternoonStoreLocation: shift === 'afternoon' ? storeLocation : nextDay.afternoonStoreLocation,
        },
      };
    });
  };

  const handleSaveSchedule = async () => {
    if (!selectedEmployee) {
      message.warning('Vui lòng chọn nhân viên để lưu lịch làm việc');
      return;
    }

    if (isArchiveWeek) {
      message.error('Không thể lưu lại dữ liệu lịch đã archive');
      return;
    }

    setSavingSchedule(true);
    try {
      const deadlineStatus = await getDeadlineStatus(selectedWeek.toDate());
      const hasExisting = Boolean(currentEmployeeSchedule);
      if (deadlineStatus.isPast && !hasExisting) {
        message.error(`Đã quá hạn đăng ký mới. Hạn đăng ký: ${deadlineStatus.deadlineText}`);
        return;
      }

      const defaultStore = storeOptions[0]?.value;
      const cleanSchedule: Record<number, DaySchedule> = {};

      Object.keys(selectedSchedule).forEach((key) => {
        const dayIndex = Number(key);
        const day = selectedSchedule[dayIndex];

        if (!day) {
          return;
        }

        const morningActive = day.morning && day.morningPosition !== 'none';
        const afternoonActive = day.afternoon && day.afternoonPosition !== 'none';

        if (!morningActive && !afternoonActive) {
          return;
        }

        cleanSchedule[dayIndex] = {
          morning: morningActive,
          afternoon: afternoonActive,
          morningPosition: morningActive ? day.morningPosition : 'none',
          afternoonPosition: afternoonActive ? day.afternoonPosition : 'none',
          morningStoreLocation: morningActive ? day.morningStoreLocation ?? defaultStore : undefined,
          afternoonStoreLocation: afternoonActive ? day.afternoonStoreLocation ?? defaultStore : undefined,
        };
      });

      await workScheduleService.upsertSchedule({
        userId: selectedEmployee.userId,
        userName: selectedEmployee.name,
        userAvatar: selectedEmployee.avatar,
        weekOf: normalizeWeekStart(selectedWeek.toDate()),
        schedule: cleanSchedule,
      });

      message.success('Lưu lịch làm việc thành công');
      const selectedWeekKey = getWeekKey(selectedWeek.toDate());
      weekScheduleCacheRef.current.delete(selectedWeekKey);
      await loadSchedulesByWeek(selectedWeek.toDate(), { force: true });

      if (selectedWeek.isSame(getCurrentWeek(), 'day')) {
        currentWeekCacheRef.current = null;
        await loadCurrentWeekSchedules(true);
      }
    } catch (error) {
      message.error('Không thể lưu lịch làm việc');
    } finally {
      setSavingSchedule(false);
    }
  };

  const handleSaveConfig = async (values: CalendarConfig) => {
    setSavingConfig(true);
    try {
      await calendarConfigService.updateCalendarConfig(values, 'admin');
      message.success('Cập nhật cấu hình lịch làm việc thành công');
      await loadConfig(true);
      deadlineCheckCacheRef.current.clear();
    } catch (error) {
      message.error('Không thể cập nhật cấu hình lịch làm việc');
    } finally {
      setSavingConfig(false);
    }
  };

  const handleResetSchedules = () => {
    Modal.confirm({
      title: 'Xác nhận reset lịch tuần',
      content: 'Tất cả lịch hiện tại sẽ được chuyển sang kho lưu trữ. Bạn có chắc chắn muốn tiếp tục?',
      okText: 'Reset',
      okType: 'danger',
      cancelText: 'Hủy',
      async onOk() {
        setResettingSchedules(true);
        try {
          await workScheduleService.resetAllSchedules('admin');
          message.success('Đã reset và archive toàn bộ lịch hiện tại');
          weekScheduleCacheRef.current.clear();
          currentWeekCacheRef.current = null;
          await loadSchedulesByWeek(selectedWeek.toDate(), { force: true });
          await loadCurrentWeekSchedules(true);
        } catch (error) {
          message.error('Không thể reset lịch');
        } finally {
          setResettingSchedules(false);
        }
      },
    });
  };

  const handleCleanupDuplicates = async () => {
    setCleaningDuplicates(true);
    try {
      await workScheduleService.cleanupDuplicateSchedulesForWeek(selectedWeek.toDate());
      message.success('Đã dọn lịch trùng của tuần đang chọn');
      const selectedWeekKey = getWeekKey(selectedWeek.toDate());
      weekScheduleCacheRef.current.delete(selectedWeekKey);
      await loadSchedulesByWeek(selectedWeek.toDate(), { force: true });

      if (selectedWeek.isSame(getCurrentWeek(), 'day')) {
        currentWeekCacheRef.current = null;
        await loadCurrentWeekSchedules(true);
      }
    } catch (error) {
      message.error('Không thể dọn lịch trùng');
    } finally {
      setCleaningDuplicates(false);
    }
  };

  const summaryColumns: ColumnsType<ScheduleEntry> = [
    {
      title: 'Nhân viên',
      dataIndex: 'userName',
      key: 'userName',
      width: 200,
      render: (_: string, record) => (
        <Space>
          <span>{record.userName}</span>
          {record.userId === selectedUserId && <Tag color="blue">Đang chọn</Tag>}
        </Space>
      ),
    },
    {
      title: 'Tổng ca',
      key: 'totalShifts',
      width: 90,
      render: (_, record) => countShiftOfSchedule(record.schedule),
    },
    {
      title: 'Chi tiết',
      key: 'details',
      render: (_, record) => (
        <Button type="link" onClick={() => setDetailSchedule(record)}>
          Xem chi tiết
        </Button>
      ),
    },
  ];

  const getPositionIcon = (position: WorkPosition): string => {
    if (position === 'table') {
      return '🍽️';
    }
    if (position === 'bar') {
      return '🍹';
    }
    if (position === 'security') {
      return '🛡️';
    }
    return '';
  };

  const getCellData = (
    dayData: DaySchedule | undefined,
    storeFilter: string
  ): { code: 'A' | 'S' | 'C' | 'O'; positions: WorkPosition[] } => {
    if (!dayData) {
      return { code: 'O', positions: [] };
    }

    const morningMatched =
      dayData.morning && (storeFilter === 'all' || dayData.morningStoreLocation === storeFilter);
    const afternoonMatched =
      dayData.afternoon && (storeFilter === 'all' || dayData.afternoonStoreLocation === storeFilter);

    const positions: WorkPosition[] = [];
    if (morningMatched && dayData.morningPosition !== 'none') {
      positions.push(dayData.morningPosition);
    }
    if (afternoonMatched && dayData.afternoonPosition !== 'none') {
      positions.push(dayData.afternoonPosition);
    }

    const uniquePositions = positions.filter((item, index, array) => array.indexOf(item) === index);

    if (morningMatched && afternoonMatched) {
      return { code: 'A', positions: uniquePositions };
    }

    if (morningMatched) {
      return { code: 'S', positions: uniquePositions };
    }

    if (afternoonMatched) {
      return { code: 'C', positions: uniquePositions };
    }

    return { code: 'O', positions: [] };
  };

  return (
    <AdminLayout>
      <div className="admin-page-header">
        <div className="admin-page-title">
          <h1>Quản lý lịch làm việc</h1>
          <Tag color="blue">{weekSchedules.length} lịch tuần</Tag>
          {isArchiveWeek && <Tag color="orange">Dữ liệu archive</Tag>}
        </div>
      </div>

      <Tabs
        activeKey={activeTab}
        onChange={(key) => setActiveTab(key as TabKey)}
        items={[
          {
            key: 'registration',
            label: (
              <span>
                <CalendarOutlined /> Đăng ký lịch làm
              </span>
            ),
            children: (
              <>
                <div className="admin-search-filters" style={{ marginBottom: 16 }}>
                  <DatePicker
                    picker="week"
                    value={selectedWeek}
                    onChange={handleWeekChange}
                    allowClear={false}
                  />
                  <Select
                    style={{ minWidth: 260 }}
                    value={selectedUserId || undefined}
                    placeholder="Chọn nhân viên"
                    onChange={(value) => setSelectedUserId(value)}
                    options={employees.map((item) => ({ label: `${item.name} (${item.userId})`, value: item.userId }))}
                  />
                  <Button icon={<ReloadOutlined />} onClick={() => loadSchedulesByWeek(selectedWeek.toDate(), { force: true })}>
                    Tải lịch tuần
                  </Button>
                </div>

                <div className="admin-form-section" style={{ padding: 16 }}>
                  <div className="admin-form-section-title" style={{ marginBottom: 16 }}>
                    Lịch đăng ký theo tuần {selectedWeek.format('DD/MM/YYYY')}
                  </div>

                  {!selectedEmployee ? (
                    <Text type="secondary">Vui lòng chọn nhân viên để đăng ký lịch.</Text>
                  ) : (
                    <Table
                      rowKey={(record) => String(record.dayIndex)}
                      pagination={false}
                      dataSource={DAYS_OF_WEEK.map((label, dayIndex) => ({
                        key: dayIndex,
                        dayIndex,
                        label,
                        dayData: selectedSchedule[dayIndex] ?? emptyDaySchedule(),
                      }))}
                      columns={[
                        {
                          title: 'Ngày',
                          dataIndex: 'label',
                          width: 110,
                        },
                        {
                          title: 'Ca sáng',
                          key: 'morning',
                          render: (_, record) => (
                            <Space direction="vertical" size={4}>
                              <Switch
                                checked={record.dayData.morning}
                                onChange={(checked) => toggleShift(record.dayIndex, 'morning', checked)}
                              />
                              <Select
                                style={{ width: 130 }}
                                size="small"
                                value={record.dayData.morningPosition}
                                disabled={!record.dayData.morning}
                                options={[
                                  { label: 'Table', value: 'table' },
                                  { label: 'Bar', value: 'bar' },
                                  { label: 'Security', value: 'security' },
                                  { label: 'Nghỉ', value: 'none' },
                                ]}
                                onChange={(value) => updatePosition(record.dayIndex, 'morning', value)}
                              />
                              <Select
                                style={{ width: 160 }}
                                size="small"
                                value={record.dayData.morningStoreLocation}
                                placeholder="Cửa hàng"
                                disabled={!record.dayData.morning}
                                options={storeOptions}
                                onChange={(value) => updateStoreLocation(record.dayIndex, 'morning', value)}
                              />
                            </Space>
                          ),
                        },
                        {
                          title: 'Ca chiều',
                          key: 'afternoon',
                          render: (_, record) => (
                            <Space direction="vertical" size={4}>
                              <Switch
                                checked={record.dayData.afternoon}
                                onChange={(checked) => toggleShift(record.dayIndex, 'afternoon', checked)}
                              />
                              <Select
                                style={{ width: 130 }}
                                size="small"
                                value={record.dayData.afternoonPosition}
                                disabled={!record.dayData.afternoon}
                                options={[
                                  { label: 'Table', value: 'table' },
                                  { label: 'Bar', value: 'bar' },
                                  { label: 'Security', value: 'security' },
                                  { label: 'Nghỉ', value: 'none' },
                                ]}
                                onChange={(value) => updatePosition(record.dayIndex, 'afternoon', value)}
                              />
                              <Select
                                style={{ width: 160 }}
                                size="small"
                                value={record.dayData.afternoonStoreLocation}
                                placeholder="Cửa hàng"
                                disabled={!record.dayData.afternoon}
                                options={storeOptions}
                                onChange={(value) => updateStoreLocation(record.dayIndex, 'afternoon', value)}
                              />
                            </Space>
                          ),
                        },
                      ]}
                      scroll={{ x: 760 }}
                    />
                  )}

                  <div style={{ marginTop: 16 }}>
                    <Button type="primary" icon={<SaveOutlined />} onClick={handleSaveSchedule} loading={savingSchedule}>
                      Lưu lịch tuần
                    </Button>
                  </div>
                </div>
              </>
            ),
          },
          {
            key: 'summary',
            label: (
              <span>
                <CalendarOutlined /> Tổng hợp lịch
              </span>
            ),
            children: (
              <>
                <div className="admin-form-section" style={{ padding: 16, marginBottom: 16 }}>
                  <div className="admin-page-header" style={{ marginBottom: 12 }}>
                    <div className="admin-form-section-title" style={{ marginBottom: 0, borderBottom: 'none', paddingBottom: 0 }}>
                      Lịch tuần hiện tại của quán ({getCurrentWeek().format('DD/MM')} - {getCurrentWeek().add(6, 'day').format('DD/MM')})
                    </div>
                    <Button icon={<ReloadOutlined />} onClick={() => loadCurrentWeekSchedules(true)} loading={loadingCurrentWeekSchedules}>
                      Tải lại tuần hiện tại
                    </Button>
                  </div>

                  <Table
                    className="admin-table"
                    loading={loadingCurrentWeekSchedules}
                    rowKey="userId"
                    pagination={false}
                    dataSource={currentWeekMatrixRows}
                    scroll={{ x: 980 }}
                    columns={[
                      {
                        title: 'Thành viên',
                        dataIndex: 'userName',
                        fixed: 'left',
                        width: 240,
                        render: (_, record) => (
                          <Space>
                            <div
                              style={{
                                width: 34,
                                height: 34,
                                borderRadius: 17,
                                background: '#f0f0f0',
                                overflow: 'hidden',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontWeight: 600,
                                color: '#8c8c8c',
                              }}
                            >
                              {record.userAvatar ? (
                                <img src={record.userAvatar} alt={record.userName} style={{ width: 34, height: 34, objectFit: 'cover' }} />
                              ) : (
                                record.userName.slice(0, 1).toUpperCase()
                              )}
                            </div>
                            <div>{record.userName}</div>
                          </Space>
                        ),
                      },
                      ...DAY_SHORT_LABELS.map((dayLabel, dayIndex) => ({
                        title: dayLabel,
                        key: `day-${dayIndex}`,
                        width: 95,
                        align: 'center' as const,
                        render: (_: unknown, record: { schedule: Record<number, DaySchedule> }) => {
                          const cellData = getCellData(record.schedule[dayIndex], summaryStoreFilter);
                          const isOff = cellData.code === 'O';

                          return (
                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                              <div
                                style={{
                                  width: 34,
                                  height: 34,
                                  borderRadius: 17,
                                  background: isOff ? '#f5f5f5' : '#1890ff',
                                  color: isOff ? '#8c8c8c' : '#fff',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  fontWeight: 600,
                                }}
                              >
                                {cellData.code}
                              </div>
                              <div style={{ minHeight: 18, fontSize: 14 }}>
                                {cellData.positions.map((position) => getPositionIcon(position)).join(' ')}
                              </div>
                            </div>
                          );
                        },
                      })),
                    ]}
                  />

                  <div style={{ marginTop: 16 }}>
                    <Space wrap>
                      <Tag color="blue">A: Cả ngày</Tag>
                      <Tag color="blue">S: Ca sáng</Tag>
                      <Tag color="blue">C: Ca chiều</Tag>
                      <Tag>O: Nghỉ</Tag>
                      <Tag>🍽️: Bàn</Tag>
                      <Tag>🍹: Bar</Tag>
                      <Tag>🛡️: Bảo vệ</Tag>
                    </Space>
                  </div>
                </div>

                <div className="admin-search-filters" style={{ marginBottom: 16 }}>
                  <DatePicker
                    picker="week"
                    value={selectedWeek}
                    onChange={handleWeekChange}
                    allowClear={false}
                  />
                  <Select
                    style={{ minWidth: 220 }}
                    value={summaryStoreFilter}
                    options={[{ label: 'Tất cả cửa hàng', value: 'all' }, ...storeOptions]}
                    onChange={(value) => setSummaryStoreFilter(value)}
                  />
                  <Button icon={<ReloadOutlined />} onClick={() => loadSchedulesByWeek(selectedWeek.toDate(), { force: true })}>
                    Làm mới dữ liệu
                  </Button>
                </div>

                <Table
                  className="admin-table"
                  rowKey={(record) => `${record.userId}-${record.weekOf.getTime()}`}
                  loading={loadingSchedules}
                  dataSource={summarySchedules}
                  columns={summaryColumns}
                  pagination={{ pageSize: 10, showSizeChanger: true }}
                />
              </>
            ),
          },
          {
            key: 'settings',
            label: (
              <span>
                <SettingOutlined /> Cài đặt lịch
              </span>
            ),
            children: (
              <Form form={configForm} layout="vertical" onFinish={handleSaveConfig} className="admin-form-container">
                <div className="admin-form-section" style={{ padding: 16 }}>
                  <div className="admin-form-section-title">Cấu hình giới hạn đăng ký</div>
                  <div className="admin-form-row">
                    <Form.Item name="weekdayShiftCapacity" label="Tổng số slot ngày thường" rules={[{ required: true, message: 'Vui lòng nhập giới hạn' }]}>
                      <InputNumber min={1} style={{ width: '100%' }} />
                    </Form.Item>
                    <Form.Item name="weekendShiftCapacity" label="Tổng số slot cuối tuần" rules={[{ required: true, message: 'Vui lòng nhập giới hạn' }]}>
                      <InputNumber min={1} style={{ width: '100%' }} />
                    </Form.Item>
                    <Form.Item name="weekdayTableCapacity" label="Table ngày thường" rules={[{ required: true, message: 'Vui lòng nhập giới hạn' }]}>
                      <InputNumber min={0} style={{ width: '100%' }} />
                    </Form.Item>
                    <Form.Item name="weekdayBarCapacity" label="Bar ngày thường" rules={[{ required: true, message: 'Vui lòng nhập giới hạn' }]}>
                      <InputNumber min={0} style={{ width: '100%' }} />
                    </Form.Item>
                    <Form.Item name="weekdaySecurityCapacity" label="Security ngày thường" rules={[{ required: true, message: 'Vui lòng nhập giới hạn' }]}>
                      <InputNumber min={0} style={{ width: '100%' }} />
                    </Form.Item>
                    <Form.Item name="weekendTableCapacity" label="Table cuối tuần" rules={[{ required: true, message: 'Vui lòng nhập giới hạn' }]}>
                      <InputNumber min={0} style={{ width: '100%' }} />
                    </Form.Item>
                    <Form.Item name="weekendBarCapacity" label="Bar cuối tuần" rules={[{ required: true, message: 'Vui lòng nhập giới hạn' }]}>
                      <InputNumber min={0} style={{ width: '100%' }} />
                    </Form.Item>
                    <Form.Item name="weekendSecurityCapacity" label="Security cuối tuần" rules={[{ required: true, message: 'Vui lòng nhập giới hạn' }]}>
                      <InputNumber min={0} style={{ width: '100%' }} />
                    </Form.Item>
                    <Form.Item name="expectedWeeklyUsers" label="Số nhân viên kỳ vọng mỗi tuần" rules={[{ required: true, message: 'Vui lòng nhập số lượng' }]}>
                      <InputNumber min={0} style={{ width: '100%' }} />
                    </Form.Item>
                    <Form.Item
                      name="storeLocations"
                      label="Danh sách cửa hàng"
                      rules={[{ required: true, message: 'Vui lòng nhập ít nhất 1 cửa hàng' }]}
                    >
                      <Select mode="tags" tokenSeparators={[',']} placeholder="Nhập tên cửa hàng" />
                    </Form.Item>
                  </div>
                </div>

                <div className="admin-form-section" style={{ padding: 16 }}>
                  <div className="admin-form-section-title">Deadline & reset hệ thống</div>
                  <div className="admin-form-row">
                    <Form.Item name="scheduleDeadlineDay" label="Ngày deadline (0=Thứ 2 ... 6=CN)" rules={[{ required: true, message: 'Vui lòng nhập ngày deadline' }]}>
                      <InputNumber min={0} max={6} style={{ width: '100%' }} />
                    </Form.Item>
                    <Form.Item name="scheduleDeadlineHour" label="Giờ deadline" rules={[{ required: true, message: 'Vui lòng nhập giờ deadline' }]}>
                      <InputNumber min={0} max={23} style={{ width: '100%' }} />
                    </Form.Item>
                    <Form.Item name="scheduleDeadlineMinute" label="Phút deadline" rules={[{ required: true, message: 'Vui lòng nhập phút deadline' }]}>
                      <InputNumber min={0} max={59} style={{ width: '100%' }} />
                    </Form.Item>
                    <Form.Item name="scheduleResetDay" label="Ngày reset (0=Thứ 2 ... 6=CN)" rules={[{ required: true, message: 'Vui lòng nhập ngày reset' }]}>
                      <InputNumber min={0} max={6} style={{ width: '100%' }} />
                    </Form.Item>
                    <Form.Item name="scheduleResetHour" label="Giờ reset" rules={[{ required: true, message: 'Vui lòng nhập giờ reset' }]}>
                      <InputNumber min={0} max={23} style={{ width: '100%' }} />
                    </Form.Item>
                    <Form.Item name="scheduleResetMinute" label="Phút reset" rules={[{ required: true, message: 'Vui lòng nhập phút reset' }]}>
                      <InputNumber min={0} max={59} style={{ width: '100%' }} />
                    </Form.Item>
                    <Form.Item name="calendarEnabled" label="Bật/tắt đăng ký lịch" valuePropName="checked">
                      <Switch checkedChildren="Bật" unCheckedChildren="Tắt" />
                    </Form.Item>
                  </div>
                </div>

                <Space>
                  <Button type="primary" htmlType="submit" loading={savingConfig} icon={<SaveOutlined />}>
                    Lưu cấu hình
                  </Button>
                  <Button onClick={handleCleanupDuplicates} loading={cleaningDuplicates} icon={<DeleteOutlined />}>
                    Dọn lịch trùng
                  </Button>
                  <Button danger onClick={handleResetSchedules} loading={resettingSchedules}>
                    Reset toàn bộ lịch
                  </Button>
                </Space>
              </Form>
            ),
          },
        ]}
      />

      <Modal
        title={detailSchedule ? `Chi tiết lịch: ${detailSchedule.userName}` : 'Chi tiết lịch'}
        open={Boolean(detailSchedule)}
        onCancel={() => setDetailSchedule(null)}
        footer={null}
        width={760}
      >
        {detailSchedule && (
          <Table
            rowKey={(record) => String(record.dayIndex)}
            pagination={false}
            dataSource={DAYS_OF_WEEK.map((label, dayIndex) => ({
              dayIndex,
              label,
              data: detailSchedule.schedule[dayIndex] ?? emptyDaySchedule(),
            }))}
            columns={[
              { title: 'Ngày', dataIndex: 'label', width: 120 },
              {
                title: 'Ca sáng',
                render: (_, record) =>
                  record.data.morning
                    ? `${getPositionText(record.data.morningPosition)} - ${record.data.morningStoreLocation ?? 'Chưa chọn cửa hàng'}`
                    : 'Nghỉ',
              },
              {
                title: 'Ca chiều',
                render: (_, record) =>
                  record.data.afternoon
                    ? `${getPositionText(record.data.afternoonPosition)} - ${record.data.afternoonStoreLocation ?? 'Chưa chọn cửa hàng'}`
                    : 'Nghỉ',
              },
            ]}
          />
        )}
      </Modal>
    </AdminLayout>
  );
};

export default ScheduleRegistrationEngine;
